import React from 'react';
import { 
  ArrowLeft, CheckCircle, XCircle, AlertCircle, Clock, 
  PackageCheck, Microscope, ClipboardCheck, Truck, CalendarCheck,
  User, Calendar, ClipboardList, FileText, Image, AlertTriangle
} from 'lucide-react';
import { inspectionRecords, inspectionTypes, inspectionResults, inspectionPlans, nonconformanceRecords } from '../../data/qualityControlData';
import { products } from '../../data/inventoryData';

const InspectionDetail = ({ inspectionId, onBack, onViewNonconformance }) => {
  // Find inspection record
  const inspection = inspectionRecords.find(i => i.id === inspectionId);
  
  if (!inspection) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium mb-2">找不到檢驗記錄</h2>
        <p className="text-gray-500 mb-4">無法找到指定的檢驗記錄</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          返回
        </button>
      </div>
    );
  }
  
  // Get product details
  const product = products.find(p => p.id === inspection.product_id) || { name: '未知產品', sku: '' };
  
  // Get inspection type details
  const inspectionType = inspectionTypes.find(t => t.id === inspection.inspection_type) || { name: '其他檢驗', color: '#6B7280' };
  
  // Get result details
  const resultDetails = inspectionResults.find(r => r.id === inspection.result) || { name: '未知', color: '#6B7280' };
  
  // Get inspection plan
  const plan = inspectionPlans.find(p => p.id === inspection.plan_id);
  
  // Get nonconformance records
  const nonconformances = nonconformanceRecords.filter(nc => nc.inspection_id === inspection.id);
  
  // Get result icon
  const getResultIcon = (result) => {
    switch(result) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'conditional':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };
  
  // Get inspection type icon
  const getInspectionTypeIcon = (type) => {
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'in-process':
        return <Microscope className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'outgoing':
        return <Truck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'periodic':
        return <CalendarCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      default:
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">檢驗詳情</h1>
        </div>
        <div 
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" 
          style={{ 
            backgroundColor: `${resultDetails.color}20`,
            color: resultDetails.color 
          }}
        >
          {getResultIcon(inspection.result)}
          <span className="ml-1">{resultDetails.name}</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Inspection Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex items-center">
              <div className="p-2 rounded-md mr-3" style={{ backgroundColor: `${inspectionType.color}20` }}>
                {getInspectionTypeIcon(inspection.inspection_type)}
              </div>
              <h2 className="text-lg font-medium">{inspectionType.name} - {inspection.id}</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">基本資訊</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <User className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">檢驗人員</div>
                        <div className="text-sm text-gray-600">{inspection.inspector}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">檢驗日期</div>
                        <div className="text-sm text-gray-600">{inspection.inspection_date}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ClipboardList className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">檢驗計劃</div>
                        <div className="text-sm text-gray-600">{plan ? plan.name : '未指定檢驗計劃'}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">產品資訊</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium">產品名稱</div>
                      <div className="text-sm text-gray-600">{product.name}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">產品編號</div>
                      <div className="text-sm text-gray-600">{product.sku}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm font-medium">檢驗數量</div>
                        <div className="text-sm text-gray-600">{inspection.quantity_inspected}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium">合格數量</div>
                        <div className="text-sm text-gray-600">{inspection.quantity_passed}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium">不合格數量</div>
                        <div className="text-sm text-gray-600">{inspection.quantity_failed}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {inspection.notes && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">備註</h3>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                    {inspection.notes}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Checkpoint Results */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">檢驗項目結果</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3 text-left">檢驗項目</th>
                    <th className="px-6 py-3 text-left">檢驗方法</th>
                    <th className="px-6 py-3 text-left">檢驗標準</th>
                    <th className="px-6 py-3 text-left">檢驗結果</th>
                    <th className="px-6 py-3 text-left">備註</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inspection.checkpoint_results.map(checkpoint => {
                    const checkpointInfo = plan?.checkpoints.find(cp => cp.id === checkpoint.checkpoint_id);
                    const resultDetails = inspectionResults.find(r => r.id === checkpoint.result) || { name: '未知', color: '#6B7280' };
                    
                    return (
                      <tr key={checkpoint.checkpoint_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {checkpointInfo?.name || checkpoint.checkpoint_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {checkpointInfo?.method || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {checkpointInfo?.criteria || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span 
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                              style={{ 
                                backgroundColor: `${resultDetails.color}20`,
                                color: resultDetails.color 
                              }}
                            >
                              {getResultIcon(checkpoint.result)}
                              <span className="ml-1">{resultDetails.name}</span>
                            </span>
                            {checkpoint.value && (
                              <span className="ml-2 text-sm text-gray-500">{checkpoint.value}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {checkpoint.notes || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Images */}
          {inspection.images && inspection.images.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">檢驗照片</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {inspection.images.map(image => (
                    <div key={image.id} className="rounded-lg overflow-hidden border border-gray-200">
                      <img 
                        src={image.url} 
                        alt={image.description} 
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-3 bg-gray-50">
                        <p className="text-sm text-gray-700">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Nonconformance Summary */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">不合格品處理</h2>
            </div>
            {nonconformances.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {nonconformances.map(nc => (
                  <div key={nc.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium">{nc.id}</h3>
                        <p className="text-sm text-gray-600 mt-1">{nc.description}</p>
                      </div>
                      <span 
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${nc.severity === 'minor' ? 'bg-yellow-100 text-yellow-800' : 
                            nc.severity === 'major' ? 'bg-orange-100 text-orange-800' : 
                            'bg-red-100 text-red-800'}`}
                      >
                        {nc.severity === 'minor' ? '輕微' : 
                          nc.severity === 'major' ? '重大' : '嚴重'}
                      </span>
                    </div>
                    <div className="mt-2 flex justify-between items-center">
                      <div>
                        <span className="text-xs text-gray-500">數量: {nc.quantity}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-xs text-gray-500">
                          處理方式: 
                          {nc.disposition === 'return-to-supplier' && '退回供應商'}
                          {nc.disposition === 'rework' && '返工'}
                          {nc.disposition === 'scrap' && '報廢'}
                          {nc.disposition === 'use-as-is' && '特採'}
                        </span>
                      </div>
                      <button 
                        onClick={() => onViewNonconformance(nc.id)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        查看詳情
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                無不合格品記錄
              </div>
            )}
          </div>
          
          {/* Corrective Actions */}
          {inspection.corrective_actions && inspection.corrective_actions.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">改善措施</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {inspection.corrective_actions.map(action => (
                  <div key={action.id} className="p-4">
                    <h3 className="text-sm font-medium">{action.action}</h3>
                    <div className="mt-2 flex flex-wrap gap-y-2 gap-x-4 text-xs text-gray-500">
                      <span>負責單位: {action.responsible}</span>
                      <span>期限: {action.deadline}</span>
                      <span className={`
                        ${action.status === 'completed' ? 'text-green-600' : 
                          action.status === 'in-progress' ? 'text-blue-600' : 'text-gray-600'}
                      `}>
                        狀態: 
                        {action.status === 'completed' && '已完成'}
                        {action.status === 'in-progress' && '進行中'}
                        {action.status === 'pending' && '待處理'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Reference Info */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">參考資訊</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium">參考單據類型</div>
                  <div className="text-sm text-gray-600">
                    {inspection.reference_type === 'purchase_receipt' && '採購收貨單'}
                    {inspection.reference_type === 'production_order' && '生產訂單'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium">參考單據編號</div>
                  <div className="text-sm text-gray-600">{inspection.reference_id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionDetail;
